// import node module libraries
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';

// import custom components
// import bootstrap icons
import { ExclamationTriangleFill } from 'react-bootstrap-icons';
import { useEffect, useState } from 'react';
import PersonnelService from '../../services/PersonnelService';
import ContratService from '../../services/ContratService';

const OffcanvasAddContratForm = ({ onClick }) => {
	const [validated, setValidated] = useState(false);
	const [feedbackMessages, setFeedbackMessages] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [personnelOptions, setPersonnelOptions] = useState([]);

	useEffect(() => {
		const fetchPersonnelOptions = async () => {
			try {
				const personnels = await PersonnelService.getPersonnelOptions();
				setPersonnelOptions(personnels);
			} catch (error) {
				console.error('Error fetching personnels options', error);
			}
		};

		fetchPersonnelOptions();
	}, []);

	const PERSONNEL_CHOICES = personnelOptions;

	const TYPE_CHOICES = [
		{ value: 'cdi', label: 'CDI' },
		{ value: 'cdd', label: 'CDD' },
		{ value: 'stage', label: 'Stage' }
	];

	const handleSubmit = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;

		if (form.checkValidity() === false) {
			event.stopPropagation();
		} else {
			try {
				const contratData = new FormData();

				// Add all your form data to the FormData
				contratData.append('type', form.type.value);
				contratData.append('date_debut', form.date_debut.value);
				contratData.append('date_fin', form.date_fin.value);
				contratData.append('salaire_brut', form.salaire_brut.value);
				contratData.append('personnel', form.personnel.value);

				// Call the ContratService to create the contrat
				const response = await ContratService.createContrat(contratData);

				// Reset the form and state variables after successful creation
				form.reset();
				setValidated(false);
				setFeedbackMessages({});

				// Redirect the user to the list of contrats
				window.location.href = '/administration/personnel/contrats';
			} catch (error) {
				console.error('Erreur lors de la création du contrat', error);

				if (error.response && error.response.data) {
					const apiErrors = error.response.data;

					// If there are field-specific errors
					if (typeof apiErrors === 'object' && !Array.isArray(apiErrors)) {
						const newFeedbackMessages = {};

						for (const field in apiErrors) {
							newFeedbackMessages[field] = apiErrors[field][0];
						}

						setFeedbackMessages(newFeedbackMessages);
					}

					// If there's a general error detail message
					if (apiErrors.detail) {
						setAlertMessage(apiErrors.detail[0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					} else if (
						apiErrors['Il ya erreur'] &&
						apiErrors['Il ya erreur'][0]
					) {
						setAlertMessage(apiErrors['Il ya erreur'][0]);
						setShowAlert(true);

						// Hide the alert after 5 seconds
						setTimeout(() => {
							setShowAlert(false);
						}, 10000);
					}
				} else {
					alert(
						"Une erreur s'est produite lors de la soumission du formulaire."
					);
				}
			}
		}

		setValidated(true);
	};

	return (
		<Form
			className="mb-3"
			noValidate
			validated={validated}
			onSubmit={handleSubmit}
		>
			{showAlert && (
				<Alert variant="danger">
					<ExclamationTriangleFill size={25} className="me-1" />
					{alertMessage}
				</Alert>
			)}
			<Row>
				{/* Type */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="type">
							Type de contrat <span className="text-danger">*</span>
						</Form.Label>
						<Form.Select
							name="type"
							required
							isInvalid={!!feedbackMessages['type']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									type: e.target.value ? '' : 'Le type contrat est requis.'
								}));
							}}
						>
							<option value="">Selectionner un type de contrat</option>
							{TYPE_CHOICES.map((option, index) => (
								<option key={index} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['type']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				<Col xs={12} className="mb-3">
					{/* Date début et fin  */}
					<Form.Group>
						<Row>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="date_debut">
									Date début <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									name="date_debut"
									type="date"
									placeholder="Selectionner la date de début"
									id="date_debut"
									required
									isInvalid={!!feedbackMessages['date_debut']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											date_debut: e.target.value
												? ''
												: 'La date de début de ce contrat est requise.'
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['date_debut']}
								</Form.Control.Feedback>
							</Col>
							<Col md={6} sm={12} className="mb-3">
								<Form.Label htmlFor="date_fin">Date fin</Form.Label>
								<Form.Control
									name="date_fin"
									type="date"
									placeholder="Selectionner la date de fin"
									id="date_fin"
									isInvalid={!!feedbackMessages['date_fin']}
									onChange={(e) => {
										setFeedbackMessages((prevMessages) => ({
											...prevMessages,
											date_fin: e.target.value
												? ''
												: "La date selectionnée n'est pas valide."
										}));
									}}
								/>
								<Form.Control.Feedback type="invalid">
									{feedbackMessages['date_fin']}
								</Form.Control.Feedback>
							</Col>
						</Row>
					</Form.Group>
				</Col>

				{/* Salaire brut */}
				<Col xs={12} className="mb-3">
					<Form.Group className="mb-3">
						<Form.Label htmlFor="salaire_brut">
							Salaire brut <span className="text-danger">*</span>
						</Form.Label>
						<Form.Control
							name="salaire_brut"
							type="number"
							min="25"
							placeholder="123456789 FCFA"
							id="salaire_brut"
							required
							isInvalid={!!feedbackMessages['salaire_brut']}
							onChange={(e) => {
								setFeedbackMessages((prevMessages) => ({
									...prevMessages,
									salaire_brut: e.target.value
										? ''
										: 'La valeur du salaire brut est requise.'
								}));
							}}
						/>
						<Form.Control.Feedback type="invalid">
							{feedbackMessages['salaire_brut']}
						</Form.Control.Feedback>
					</Form.Group>
				</Col>

				{/* Personnel */}
				<Col xs={12} className="mb-3">
					<Form.Label htmlFor="personnel">Personnel</Form.Label>
					<Form.Select
						name="personnel"
						required
						isInvalid={!!feedbackMessages['personnel']}
						onChange={(e) => {
							const value = e.target.value;
							const feedbackMessage = value
								? ''
								: 'Selectionner la personne concernée par ce contrat.';
						}}
					>
						<option value="">
							Selectionner la personne concernée par ce contrat
						</option>
						{PERSONNEL_CHOICES.map((choice, index) => (
							<option key={index} value={choice.value}>
								{choice.label}
							</option>
						))}
					</Form.Select>
					<Form.Control.Feedback type="invalid">
						{feedbackMessages['personnel']}
					</Form.Control.Feedback>
				</Col>

				{/* Buttons */}
				<Col xs={12} className="mt-4">
					<Button variant="success" type="submit">
						Enregistrer
					</Button>
					<Button onClick={onClick} variant="outline-danger" className="ms-2">
						Fermer
					</Button>
				</Col>
			</Row>
		</Form>
	);
};

export default OffcanvasAddContratForm;
